import { useMemo, useState, useEffect } from "react";
import { Info, Search } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Props {
  className?: string;
  label?: string;
  /** When true, render link in white for use on dark/colored backgrounds */
  onDark?: boolean;
  /** ISO 3166-1 alpha-2 country code to pin at the top. Overrides browser detection. */
  countryCode?: string | null;
}

type Row = {
  /** ISO 3166-1 alpha-2 */
  code: string;
  country: string;
  region: Region;
  /** Domestic standard card rate (per Stripe public pricing). */
  rate: string;
  /** Set when Photohawk overrides the rate for connected accounts in this country. */
  override?: boolean;
  /** Optional extra context shown under the rate. */
  note?: string;
};

type Region =
  | "North America"
  | "Latin America"
  | "Europe (EEA & UK)"
  | "Europe (other)"
  | "Asia-Pacific"
  | "Middle East & Africa";

const REGION_ORDER: Region[] = [
  "North America",
  "Latin America",
  "Europe (EEA & UK)",
  "Europe (other)",
  "Asia-Pacific",
  "Middle East & Africa",
];

// Source: stripe.com/<country>/pricing — domestic standard card rate.
// International cards, currency conversion and alternative payment methods
// typically incur additional fees. Country list reflects Stripe's publicly
// supported merchant countries (full + invite/preview where pricing is published).
const ROWS: Row[] = [
  // North America
  { code: "US", country: "United States", region: "North America", rate: "2.9% + US$0.30" },
  { code: "CA", country: "Canada", region: "North America", rate: "2.9% + C$0.30" },
  { code: "MX", country: "Mexico", region: "North America", rate: "2.7% + MX$5.00", override: true, note: "Photohawk managed rate" },

  // Latin America
  { code: "BR", country: "Brazil", region: "Latin America", rate: "3.99% + R$0.39" },
  { code: "CL", country: "Chile", region: "Latin America", rate: "3.4% + CLP 250" },
  { code: "CO", country: "Colombia", region: "Latin America", rate: "3.4% + COP 900" },
  { code: "PE", country: "Peru", region: "Latin America", rate: "3.4% + S/.0.80" },
  { code: "UY", country: "Uruguay", region: "Latin America", rate: "3.4% + UYU 9" },
  { code: "CR", country: "Costa Rica", region: "Latin America", rate: "3.4% + ₡150" },
  { code: "DO", country: "Dominican Republic", region: "Latin America", rate: "3.4% + DOP 15" },
  { code: "EC", country: "Ecuador", region: "Latin America", rate: "3.4% + US$0.30" },
  { code: "SV", country: "El Salvador", region: "Latin America", rate: "3.4% + US$0.30" },
  { code: "GT", country: "Guatemala", region: "Latin America", rate: "3.4% + GTQ 2" },
  { code: "PY", country: "Paraguay", region: "Latin America", rate: "3.4% + PYG 1,800" },
  { code: "BO", country: "Bolivia", region: "Latin America", rate: "3.4% + BOB 2" },
  { code: "TT", country: "Trinidad & Tobago", region: "Latin America", rate: "3.4% + TTD 2" },

  // Europe — EEA & UK
  { code: "GB", country: "United Kingdom", region: "Europe (EEA & UK)", rate: "1.5% + £0.20 (UK cards)" },
  { code: "IE", country: "Ireland", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "DE", country: "Germany", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "FR", country: "France", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "ES", country: "Spain", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "IT", country: "Italy", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "NL", country: "Netherlands", region: "Europe (EEA & UK)", rate: "1.8% + €0.25 (EEA cards)" },
  { code: "BE", country: "Belgium", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "PT", country: "Portugal", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "AT", country: "Austria", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "FI", country: "Finland", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "LU", country: "Luxembourg", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "EE", country: "Estonia", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "LV", country: "Latvia", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "LT", country: "Lithuania", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "GR", country: "Greece", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "SK", country: "Slovakia", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "SI", country: "Slovenia", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "CY", country: "Cyprus", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "MT", country: "Malta", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "HR", country: "Croatia", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "BG", country: "Bulgaria", region: "Europe (EEA & UK)", rate: "1.5% + BGN 0.50 (EEA cards)" },
  { code: "RO", country: "Romania", region: "Europe (EEA & UK)", rate: "1.5% + RON 1.00 (EEA cards)" },
  { code: "HU", country: "Hungary", region: "Europe (EEA & UK)", rate: "1.5% + HUF 90 (EEA cards)" },
  { code: "CZ", country: "Czechia", region: "Europe (EEA & UK)", rate: "1.5% + 6 Kč (EEA cards)" },
  { code: "PL", country: "Poland", region: "Europe (EEA & UK)", rate: "1.5% + 1.00 zł (EEA cards)" },
  { code: "DK", country: "Denmark", region: "Europe (EEA & UK)", rate: "1.4% + 1.80 kr (EEA cards)" },
  { code: "SE", country: "Sweden", region: "Europe (EEA & UK)", rate: "1.4% + 1.80 kr (EEA cards)" },
  { code: "NO", country: "Norway", region: "Europe (EEA & UK)", rate: "1.4% + 1.80 kr (EEA cards)" },
  { code: "IS", country: "Iceland", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },
  { code: "LI", country: "Liechtenstein", region: "Europe (EEA & UK)", rate: "1.5% + €0.25 (EEA cards)" },

  // Europe — other
  { code: "CH", country: "Switzerland", region: "Europe (other)", rate: "2.9% + CHF 0.30" },
  { code: "GI", country: "Gibraltar", region: "Europe (other)", rate: "1.5% + £0.20" },

  // Asia-Pacific
  { code: "AU", country: "Australia", region: "Asia-Pacific", rate: "1.75% + A$0.30 (domestic)" },
  { code: "NZ", country: "New Zealand", region: "Asia-Pacific", rate: "2.7% + NZ$0.30 (domestic)" },
  { code: "JP", country: "Japan", region: "Asia-Pacific", rate: "3.6% (no fixed fee)" },
  { code: "SG", country: "Singapore", region: "Asia-Pacific", rate: "3.4% + S$0.50" },
  { code: "HK", country: "Hong Kong", region: "Asia-Pacific", rate: "3.4% + HK$2.35" },
  { code: "IN", country: "India", region: "Asia-Pacific", rate: "2% + ₹3 (domestic)" },
  { code: "MY", country: "Malaysia", region: "Asia-Pacific", rate: "2.7% + RM 1.00", override: true, note: "Photohawk managed rate" },
  { code: "PH", country: "Philippines", region: "Asia-Pacific", rate: "2.7% + ₱10.00", override: true, note: "Photohawk managed rate" },
  { code: "TH", country: "Thailand", region: "Asia-Pacific", rate: "3.65% + ฿10" },
  { code: "ID", country: "Indonesia", region: "Asia-Pacific", rate: "2.9% + Rp 2,000" },
  { code: "VN", country: "Vietnam", region: "Asia-Pacific", rate: "3.4% + ₫3,000" },
  { code: "TW", country: "Taiwan", region: "Asia-Pacific", rate: "2.9% + NT$10" },
  { code: "KR", country: "South Korea", region: "Asia-Pacific", rate: "2.9% + ₩300" },

  // Middle East & Africa
  { code: "AE", country: "United Arab Emirates", region: "Middle East & Africa", rate: "2.9% + AED 1.00" },
  { code: "SA", country: "Saudi Arabia", region: "Middle East & Africa", rate: "2.4% + SAR 1.00" },
  { code: "QA", country: "Qatar", region: "Middle East & Africa", rate: "2.9% + QAR 1.00" },
  { code: "BH", country: "Bahrain", region: "Middle East & Africa", rate: "2.9% + BHD 0.10" },
  { code: "KW", country: "Kuwait", region: "Middle East & Africa", rate: "2.9% + KWD 0.10" },
  { code: "OM", country: "Oman", region: "Middle East & Africa", rate: "2.9% + OMR 0.10" },
  { code: "IL", country: "Israel", region: "Middle East & Africa", rate: "2.9% + ₪1.20" },
  { code: "TR", country: "Türkiye", region: "Middle East & Africa", rate: "2.9% + ₺1.00" },
  { code: "ZA", country: "South Africa", region: "Middle East & Africa", rate: "2.9% + R 2.00" },
  { code: "KE", country: "Kenya", region: "Middle East & Africa", rate: "3.8% + KES 15" },
  { code: "NG", country: "Nigeria", region: "Middle East & Africa", rate: "3.9% + ₦100" },
  { code: "GH", country: "Ghana", region: "Middle East & Africa", rate: "3.8% + GHS 1" },
  { code: "EG", country: "Egypt", region: "Middle East & Africa", rate: "2.9% + EGP 3" },
];

function detectCountryCode(): string | null {
  if (typeof navigator === "undefined") return null;
  const langs = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean) as string[];
  for (const lang of langs) {
    try {
      // @ts-ignore - region exists on Intl.Locale
      const region = new Intl.Locale(lang).region;
      if (region && /^[A-Z]{2}$/.test(region)) return region;
    } catch {
      const m = /[-_]([A-Z]{2})$/i.exec(lang);
      if (m) return m[1].toUpperCase();
    }
  }
  return null;
}

const TransactionFeesInfo = ({ className = "", label = "Transaction fees apply", onDark = false, countryCode }: Props) => {
  const [query, setQuery] = useState("");
  const [detected, setDetected] = useState<string | null>(null);

  useEffect(() => {
    if (countryCode) {
      setDetected(countryCode.toUpperCase());
    } else {
      setDetected(detectCountryCode());
    }
  }, [countryCode]);

  const detectedRow = useMemo(
    () => (detected ? ROWS.find((r) => r.code === detected) ?? null : null),
    [detected]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ROWS;
    return ROWS.filter(
      (r) =>
        r.country.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.region.toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<Region, Row[]>();
    for (const r of filtered) {
      // Skip the detected row in grouped lists — it's pinned above.
      if (!query && detectedRow && r.code === detectedRow.code) continue;
      const list = map.get(r.region) ?? [];
      list.push(r);
      map.set(r.region, list);
    }
    return REGION_ORDER
      .map((region) => ({ region, rows: map.get(region) ?? [] }))
      .filter((g) => g.rows.length > 0);
  }, [filtered, detectedRow, query]);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 underline underline-offset-2 decoration-dotted transition-colors ${
            onDark ? "text-white/90 hover:text-white" : "text-hawk-orange hover:text-hawk-orange-dark"
          } ${className}`}
        >
          <Info className="h-3 w-3" />
          <span>{label}</span>
        </button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[92vh]">
        <div className="mx-auto w-full max-w-2xl flex flex-col min-h-0 flex-1">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="font-display">Transaction fees by country</DrawerTitle>
            <DrawerDescription>
              On Selling Mode sales, payment processing fees apply in addition to the Photohawk commission.
              Rates show the <strong>standard domestic card rate</strong>. International cards, currency
              conversion and alternative payment methods typically incur additional fees.
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or region…"
                className="pl-9"
              />
            </div>
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {!query && detectedRow && (
              <div className="mb-5">
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 px-1">
                  Your country
                </h4>
                <div className="rounded-xl border-2 border-hawk-orange/40 overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      <CountryRow row={detectedRow} highlighted />
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No countries match "{query}".
              </p>
            ) : (
              <div className="space-y-5">
                {grouped.map((group) => (
                  <div key={group.region}>
                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 px-1">
                      {group.region}
                    </h4>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          {group.rows.map((r, i) => (
                            <CountryRow key={r.code} row={r} striped={i % 2 === 1} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 space-y-3 text-xs text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Photohawk managed rate</strong> — applies when payouts are
                handled through Photohawk in Malaysia, the Philippines and Mexico. No Stripe account required.
              </p>
              <p>
                <strong className="text-foreground">Stripe connected accounts</strong> — if you connect your own
                Stripe account, fees are billed by Stripe directly and depend on your country, payment method
                and card type. Always check{" "}
                <a
                  href="https://stripe.com/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-hawk-orange hover:underline"
                >
                  stripe.com/pricing
                </a>{" "}
                for the most accurate, up-to-date rates in your country.
              </p>
              <p className="italic">Rates shown are indicative and may have changed since publication.</p>
            </div>
          </div>

          <DrawerFooter className="pt-2">
            <DrawerClose asChild>
              <Button variant="outline">Close</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const CountryRow = ({
  row,
  striped,
  highlighted,
}: {
  row: Row;
  striped?: boolean;
  highlighted?: boolean;
}) => (
  <tr
    className={`${striped ? "bg-muted/20" : ""} ${
      row.override ? "bg-hawk-orange/5" : ""
    } ${highlighted ? "bg-hawk-orange/10" : ""}`}
  >
    <td className="px-4 py-2.5 align-top">
      <div className="flex items-center gap-2 flex-wrap">
        <span>{row.country}</span>
        {row.override && (
          <span className="text-[10px] uppercase tracking-wide font-semibold text-hawk-orange bg-hawk-orange/10 px-1.5 py-0.5 rounded">
            Photohawk
          </span>
        )}
      </div>
      {row.note && <div className="text-xs text-muted-foreground mt-0.5">{row.note}</div>}
    </td>
    <td className="px-4 py-2.5 text-right font-mono text-foreground whitespace-nowrap align-top">
      {row.rate}
    </td>
  </tr>
);

export default TransactionFeesInfo;
